// Layer 3 (Transformation): pure projection for the Metallurgist's
// concentrate-quality-and-losses read (paul-feedback.A5 / #265) — three
// concentrate grades, the penalty elements (via the same `ConcentrateSpec`
// projection Quality › Impurities reads), and the three tails grades
// against target. No React, no engine mutation, nothing typed that the
// closed mass balance does not already carry (brief hard rule 3).

import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { CONCENTRATE_SPECS } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import {
  formatMeasuredQuantity,
  periodView,
  periodWindows,
  tailsLossRead,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import type { ClosureTone } from '@/versions/v1/schema/metal-accounting';
import {
  concentratePenaltyPanelModel,
  type PenaltyConcentrateGroup,
} from '@/versions/v1/modules/quality/ConcentratePenaltyPanel.transform';

export type ConcentrateTone = 'ok' | 'warn' | 'bad' | 'neutral';

export type GradeRowView = {
  id: string;
  label: string;
  valueText: string;
  targetText: string;
  targetProvenance: string;
  readingProvenance: string;
  tone: ConcentrateTone;
  statusLabel: string;
};

export type TailsRowView = {
  element: 'Pb' | 'Zn' | 'Ag';
  assayText: string;
  targetText: string;
  targetProvenance: string;
  tone: ConcentrateTone;
  statusLabel: string;
  provenance: string;
};

/** One penalty element as the Senior metallurgist landing's concentrate card
 *  draws it (polish.P27 comp A): the assay without its unit, `of` the contract
 *  limit with its unit, and the assay ÷ limit headroom meter. */
export type PenaltyMeterRowView = {
  element: string;
  valueText: string;
  limitText: string;
  /** Assay ÷ contract limit, clamped to 0–1 for the meter. */
  meterRatio: number;
  tone: ConcentrateTone;
  statusLabel: string;
  provenance: string;
};

export type PenaltyMeterGroupView = {
  id: string;
  label: string;
  rows: readonly PenaltyMeterRowView[];
};

export type ConcentrateQualityModel = {
  gradeRows: readonly GradeRowView[];
  penaltyGroups: readonly PenaltyConcentrateGroup[];
  /** The penalty groups in the landing card's meter form (same rows, same order). */
  penaltyMeterGroups: readonly PenaltyMeterGroupView[];
  tailsRows: readonly TailsRowView[];
};

const CLOSURE_TO_CONCENTRATE_TONE: Record<ClosureTone, ConcentrateTone> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  unmeasured: 'neutral',
};

const GRADE_STATUS_LABEL: Record<ConcentrateTone, string> = {
  ok: 'OK',
  warn: 'WATCH',
  bad: 'BELOW',
  neutral: 'DESIGN BASIS',
};

const TAILS_STATUS_LABEL: Record<ConcentrateTone, string> = {
  ok: 'OK',
  warn: 'WATCH',
  bad: 'OVER',
  neutral: '—',
};

function gradeToneFor(value: number | undefined, lo: number | undefined): ConcentrateTone {
  if (value === undefined || lo === undefined) return 'neutral';
  return value >= lo ? 'ok' : 'bad';
}

/** Short concentrate names for the card's row labels (`Pb con · Pb grade`). */
const CONCENTRATE_SHORT: Record<string, string> = { Pb: 'Pb con', Zn: 'Zn con' };

/** The day balance's Pb/Ag concentrate Ag grade — the product grade Metal
 *  accounting reads (SA03 via `PeriodBalance.productGrade`), K20 #717. */
function balanceAgGrade(): number | undefined {
  const view = periodView('day');
  return 'productGrade' in view ? view.productGrade.agConcOzPerT : undefined;
}

function gradeRows(): GradeRowView[] {
  const rows: GradeRowView[] = [];
  for (const spec of CONCENTRATE_SPECS) {
    for (const grade of spec.gradeSpecs) {
      const reading = grade.readingId ? readingByIdLookup(grade.readingId) : undefined;
      // Comp adjudication 9: the Ag row reads the measured balance grade with
      // its status tone against design, not the Design basis chip.
      const isAg = grade.parameter === 'Ag content';
      const value = isAg ? balanceAgGrade() ?? reading?.value : reading?.value;
      const decimals = grade.displayDecimals ?? reading?.decimals ?? 1;
      const targetValue = grade.lo ?? reading?.design;
      const tone = isAg ? gradeToneFor(value, reading?.design) : gradeToneFor(value, grade.lo);
      rows.push({
        id: `${spec.id}-${grade.parameter}`,
        label: `${CONCENTRATE_SHORT[spec.product] ?? spec.label} · ${grade.parameter}`,
        valueText: value !== undefined ? `${value.toFixed(decimals)} ${grade.unit}` : 'no reading at this seed',
        targetText:
          targetValue !== undefined
            ? grade.lo !== undefined
              ? `> ${targetValue} ${grade.unit}`
              : `design ${targetValue} ${grade.unit}`
            : '—',
        targetProvenance: grade.provenance,
        readingProvenance: isAg
          ? 'Derived — PeriodBalance day product grade, Pb/Ag concentrate × SA03 Ag assay (as Metal accounting reads it)'
          : reading?.provenance ?? '',
        tone,
        statusLabel: GRADE_STATUS_LABEL[tone],
      });
    }
  }
  return rows;
}

function tailsRowFor(element: 'Pb' | 'Zn' | 'Ag'): TailsRowView {
  const read = tailsLossRead(element, periodWindows().day);
  // One percent spacing across the card (comp A): `0.18 % Pb`, as the grades read.
  const assayText = read.tailsAssay
    ? formatMeasuredQuantity(read.tailsAssay, undefined, { decimals: element === 'Ag' ? 3 : 2 }).replace(/(\d)%/, '$1 %')
    : '—';
  const targetReading = readingByIdLookup(
    element === 'Zn' ? 'rd-spec-tails-zn' : element === 'Pb' ? 'rd-spec-tails-pb' : 'rd-spec-tails-ag',
  );
  const targetHi = targetReading?.spec?.hi;
  const targetText =
    targetHi === undefined
      ? '—'
      : element === 'Ag'
        ? `< ${targetHi.toFixed(3)} oz/t Ag`
        : `< ${targetHi.toFixed(2)} % ${element}`;
  const tone = CLOSURE_TO_CONCENTRATE_TONE[read.tailsAssayTone];

  return {
    element,
    assayText,
    targetText,
    targetProvenance: targetReading?.provenance ?? '',
    tone,
    statusLabel: TAILS_STATUS_LABEL[tone],
    provenance: read.provenance,
  };
}

/** `0.32%` → `0.32` when the limit carries the same unit (`of 0.50%`). */
function valueWithoutUnit(current: string, limit: string): string {
  const unit = limit.replace(/^[0-9.,]+\s*/, '');
  if (!unit) return current;
  const trimmed = current.endsWith(unit) ? current.slice(0, -unit.length) : current;
  return trimmed.trim();
}

function penaltyMeterGroups(groups: readonly PenaltyConcentrateGroup[]): PenaltyMeterGroupView[] {
  return groups.map((group) => ({
    id: group.id,
    label: group.label,
    rows: group.rows.map((row) => ({
      element: row.element,
      valueText: row.currentText && row.contractLimitText ? valueWithoutUnit(row.currentText, row.contractLimitText) : row.currentText ?? '—',
      limitText: row.contractLimitText ?? '—',
      meterRatio: Math.max(0, Math.min(1, row.limitRatio ?? 0)),
      tone: row.tone,
      statusLabel: row.statusLabel,
      // Two thresholds, never merged: the contract limit's source and, beside
      // it, the typical-trigger band with its own source (tooltip only).
      provenance: [
        row.contractProvenance ? `Contract limit: ${row.contractProvenance}` : undefined,
        row.triggerBandText ? `Typical trigger ${row.triggerBandText}${row.triggerProvenance ? ` (${row.triggerProvenance})` : ''}` : undefined,
        row.measurementProvenance,
      ]
        .filter(Boolean)
        .join(' · '),
    })),
  }));
}

export function concentrateQualityModel(): ConcentrateQualityModel {
  const penalty = concentratePenaltyPanelModel();
  return {
    gradeRows: gradeRows(),
    penaltyGroups: penalty.groups,
    penaltyMeterGroups: penaltyMeterGroups(penalty.groups),
    tailsRows: (['Pb', 'Zn', 'Ag'] as const).map(tailsRowFor),
  };
}
